import { useQuery } from '@tanstack/react-query';
import { nodesApi } from '@/api/nodes';
import { queryKeys } from '@/lib/queryKeys';
import { idempotencyKey } from '@/utils/idempotency';
import { useApiMutation, type MutationCallbacks } from '@/hooks/useApiMutation';
import type { AgentInstallView, DrainOutcome, EnqueueResult, EnrollmentIssued, RegisterHostRequest, RegisterNodeRequest, SshHostKeyView, SshInstallRequest } from '@/types/api';

/**
 * The panel's servers.
 *
 * `watch` keeps the list fresh for the servers screen — faster while a server
 * waits for its agent, which is the moment someone is watching for the state
 * to change. `pollMs` asks again at a fixed pace; pickers read the list once.
 */
export function useNodes({
  enabled = true,
  watch = false,
  pollMs,
}: { enabled?: boolean; watch?: boolean; pollMs?: number } = {}) {
  return useQuery({
    queryKey: queryKeys.nodes.all,
    queryFn: nodesApi.list,
    enabled,
    refetchInterval: watch
      ? (query) => (query.state.data?.some((node) => node.agentVersion === null) ? 3000 : 15000)
      : (pollMs ?? false),
  });
}

/** One server for its page (ADR-0058): asked again every quarter minute, faster while its agent is awaited. */
export function useNode(id: string) {
  return useQuery({
    queryKey: queryKeys.nodes.detail(id),
    queryFn: () => nodesApi.get(id),
    refetchInterval: (query) => (query.state.data?.agentVersion === null ? 3000 : 15000),
  });
}

/**
 * The 2d2s inventory. After a sync is queued the list is asked again until
 * `pollUntil`: the worker reads 2d2s in the background and there is no other
 * way to learn when it is done.
 */
export function useServerInventory({ enabled, pollUntil }: { enabled: boolean; pollUntil: number }) {
  return useQuery({
    queryKey: queryKeys.servers,
    queryFn: nodesApi.servers,
    enabled,
    refetchInterval: () => (Date.now() < pollUntil ? 2000 : false),
  });
}

/** Stopped copies on a server; asked again while removals queued from here are still on it. */
export function useNodeRemnants(nodeId: string, pending: ReadonlySet<string>) {
  return useQuery({
    queryKey: queryKeys.nodes.remnants(nodeId),
    queryFn: () => nodesApi.remnants(nodeId),
    refetchInterval: (query) => ((query.state.data ?? []).some((remnant) => pending.has(remnant.id)) ? 3000 : false),
  });
}

export function useSyncServers(callbacks?: MutationCallbacks<EnqueueResult, void>) {
  return useApiMutation(() => nodesApi.syncServers(idempotencyKey('servers.sync')), () => [], callbacks);
}

export function useRegisterNode(callbacks?: MutationCallbacks<EnrollmentIssued, RegisterNodeRequest>) {
  return useApiMutation(nodesApi.register, () => [queryKeys.nodes.all, queryKeys.servers], callbacks);
}

export function useRegisterHost(callbacks?: MutationCallbacks<EnrollmentIssued, RegisterHostRequest>) {
  return useApiMutation(nodesApi.registerHost, () => [queryKeys.nodes.all], callbacks);
}

/** Asks an external server's agent what it serves; the list refreshes as the answer comes. */
export function useDiscoverHost(callbacks?: MutationCallbacks<EnqueueResult, string>) {
  return useApiMutation((id: string) => nodesApi.discover(id, idempotencyKey('host.discover')), () => [queryKeys.nodes.all, queryKeys.sites.all], callbacks);
}

/** A new enrolment token for a server whose agent never connected; the old one stops working. */
export function useReissueEnrollmentToken(callbacks?: MutationCallbacks<EnrollmentIssued, string>) {
  return useApiMutation(nodesApi.reissueEnrollmentToken, () => [], callbacks);
}

/** The server's SSH host key, read before any login is sent (ADR-0036). */
export function useSshHostKey(nodeId: string, callbacks?: MutationCallbacks<SshHostKeyView, number>) {
  return useApiMutation((port: number) => nodesApi.sshHostKey(nodeId, port), () => [], callbacks);
}

export function useInstallAgent(nodeId: string, callbacks?: MutationCallbacks<AgentInstallView, SshInstallRequest>) {
  return useApiMutation((input: SshInstallRequest) => nodesApi.installAgent(nodeId, input), () => [], callbacks);
}

/** An installation as it goes: asked again every second and a half until it ends. */
export function useAgentInstall(installId: string | null) {
  return useQuery({
    queryKey: queryKeys.nodes.install(installId ?? ''),
    queryFn: () => nodesApi.agentInstall(installId!),
    enabled: installId !== null,
    refetchInterval: (query) => (query.state.data?.state === 'running' || !query.state.data ? 1_500 : false),
  });
}

export function useSetNodeScheduling(
  callbacks?: MutationCallbacks<{ schedulingState: string }, { id: string; state: 'active' | 'cordoned' }>,
) {
  return useApiMutation(({ id, state }) => nodesApi.setScheduling(id, state), () => [queryKeys.nodes.all], callbacks);
}

export function useRevokeAgent(callbacks?: MutationCallbacks<{ revoked: boolean }, { id: string; confirmation: string }>) {
  return useApiMutation(({ id, confirmation }) => nodesApi.revokeAgent(id, confirmation), () => [queryKeys.nodes.all], callbacks);
}

export function useDrainNode(
  callbacks?: MutationCallbacks<DrainOutcome, { id: string; confirmation: string; key: string }>,
) {
  return useApiMutation(
    ({ id, confirmation, key }) => nodesApi.drain(id, confirmation, key),
    () => [queryKeys.nodes.all, queryKeys.jobs.all],
    callbacks,
  );
}

/**
 * Removes a stopped copy (ADR-0021), from the server's side or the site's:
 * whichever screen asked is the one whose list is read again.
 */
export function useReclaimRemnant(
  from: { nodeId: string } | { siteId: string },
  callbacks?: MutationCallbacks<EnqueueResult, { remnantId: string; key: string }>,
) {
  return useApiMutation(
    ({ remnantId, key }) => nodesApi.reclaimRemnant(remnantId, key),
    () => [queryKeys.jobs.all, 'nodeId' in from ? queryKeys.nodes.remnants(from.nodeId) : queryKeys.site(from.siteId)],
    callbacks,
  );
}
